import { useState, useEffect } from 'react';
import Footer from '../home/components/Footer';
import Navigation from '../home/components/Navigation';
import { updateSEO, cleanupStructuredData } from '../../lib/seo';

export default function PartnersPage() {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    category: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://ranchfireradio.com';
    
    updateSEO({
      title: 'Brand Partners & Sponsorships - RanchFire Radio Partnership Opportunities',
      description: 'Partner with RanchFire Radio to reach an authentic Texas Country music audience. Explore founding partner, show sponsor, and brand partnership opportunities with loyal listeners who value quality and authenticity.',
      keywords: 'RanchFire Radio Partners, Radio Sponsorship, Texas Country Advertising, Brand Partnership, Show Sponsor, Radio Marketing',
      canonical: '/partners',
      ogTitle: 'Brand Partners & Sponsorships - RanchFire Radio',
      ogDescription: 'Reach an audience that doesn\'t just listen—they commit. Partner with RanchFire Radio.',
      ogImage: 'https://static.readdy.ai/image/f407d1ad302e1be1c54e67247a4ef201/05d65ab4d065546745607e69a877391a.png',
      structuredData: {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Brand Partners & Sponsorships",
        "description": "Partnership opportunities with RanchFire Radio for brands targeting authentic Texas Country music audiences",
        "url": `${siteUrl}/partners`,
        "publisher": {
          "@type": "Organization",
          "name": "RanchFire Radio",
          "url": siteUrl
        }
      }
    });

    return () => {
      cleanupStructuredData();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('company', formData.company);
      formBody.append('email', formData.email);
      formBody.append('category', formData.category);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5toa1j0o41p730737dg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          company: '',
          email: '',
          category: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const partnershipOptions = [
    {
      title: 'Founding Partner',
      description: 'Shape the future of RanchFire Radio. Exclusive brand integration, custom content series, and direct access to our core audience from day one.',
      icon: 'ri-star-line'
    },
    {
      title: 'Show Sponsor',
      description: 'Align your brand with specific shows and segments. Targeted reach with authentic endorsements from trusted voices in the Texas Country community.',
      icon: 'ri-radio-line'
    },
    {
      title: 'Brand Sponsor/Partner',
      description: 'Collaborative promotion for your brand. Featured spotlights, product premieres, and coordinated campaigns that reach engaged listeners.',
      icon: 'ri-briefcase-line'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <Navigation />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 min-h-[70vh] flex items-center justify-center bg-gradient-to-br from-amber-900 via-orange-800 to-red-900">
        <div className="absolute inset-0">
          <img 
            src="https://static.readdy.ai/image/f407d1ad302e1be1c54e67247a4ef201/05d65ab4d065546745607e69a877391a.png"
            alt="Partners"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white uppercase tracking-tight mb-6 font-display">
            Brands/Partners
          </h1>
          <p className="text-xl sm:text-2xl lg:text-3xl text-orange-300 font-bold mb-10 leading-relaxed max-w-3xl mx-auto">
            Reach an audience that doesn't just listen—they commit.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a 
              href="#partner-form"
              className="w-full sm:w-auto bg-orange-600 hover:bg-orange-700 text-white px-10 py-4 rounded-full font-bold text-lg uppercase tracking-wide transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-orange-600/50 whitespace-nowrap cursor-pointer"
            >
              Partner With Us
            </a>
          </div>
        </div>
      </section>

      {/* Partner Story Section */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-4xl sm:text-5xl font-black text-gray-900 uppercase tracking-tight mb-12 text-center font-display">
            Why This Audience Matters
          </h2>
          
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              RanchFire Radio listeners aren't passive scrollers. They're people who choose what they listen to, who turn the volume up when a song hits, and who remember the brands that show up in the spaces they trust.
            </p>
            
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              This is an audience rooted in loyalty. They don't bounce between stations looking for the next thing—they stay because the music matters, because the curation is real, and because they know we're not selling them something we don't believe in.
            </p>
            
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              They're working people. Veterans. Builders. Creators. Folks who value quality, authenticity, and brands that understand where they come from. They don't need to be convinced—they need to be respected.
            </p>
            
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              When they hear a brand on RanchFire, it's not background noise. It's an endorsement. It's a signal that this company gets it, that they're part of the same world, that they're worth paying attention to.
            </p>
            
            <p className="text-gray-700 text-lg leading-relaxed font-bold">
              This isn't about reach for the sake of reach. It's about reaching the right people—the ones who show up, who stick around, and who actually buy what you're selling because they trust where they heard it.
            </p>
          </div>
        </div>
      </section>

      {/* Who Listens Section */}
      <section className="py-20 sm:py-28 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-4xl sm:text-5xl font-black text-gray-900 uppercase tracking-tight mb-12 text-center font-display">
            Who Listens
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200 text-center">
              <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-music-2-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-black text-gray-900 uppercase mb-3">
                Texas Country, Lived In
              </h3>
              <p className="text-gray-700 leading-relaxed">
                A deeply loyal Texas Co and Tex-Mex Country audience whose purchasing decisions follow music, place, and weekend rituals.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200 text-center">
              <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shopping-bag-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-black text-gray-900 uppercase mb-3">
                Lifestyle-Driven Spending
              </h3>
              <p className="text-gray-700 leading-relaxed">
                From trucks and boots to beer, travel, and family outings, RanchFire listeners align spending with how they actually live.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200 text-center">
              <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-heart-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-black text-gray-900 uppercase mb-3">
                Built on Trust
              </h3>
              <p className="text-gray-700 leading-relaxed">
                When a brand fits the RanchFire lifestyle, it earns repeat business, word-of-mouth, and long-term loyalty.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Options */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-4xl sm:text-5xl font-black text-gray-900 uppercase tracking-tight mb-16 text-center font-display">
            Partnership Options
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {partnershipOptions.map((option, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8 shadow-lg border border-gray-200 hover:shadow-2xl transition-all duration-300">
                <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center mb-6">
                  <i className={`${option.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-2xl font-black text-gray-900 uppercase mb-4">
                  {option.title}
                </h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  {option.description}
                </p>
                <a 
                  href="#partner-form"
                  className="inline-block bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-full font-bold text-sm uppercase tracking-wide transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  Learn More
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partner Form */}
      <section id="partner-form" className="py-20 sm:py-28 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-black text-gray-900 uppercase tracking-tight mb-4 font-display">
              Request Info
            </h2>
            <p className="text-gray-600 text-lg">
              Let's talk about how we can work together.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 sm:p-10 shadow-lg border border-gray-200" data-readdy-form id="partner-inquiry-form">
            <div className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-bold text-gray-900 uppercase tracking-wide mb-2">
                  Name <span className="text-orange-600">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-600 text-gray-900"
                  placeholder="Your full name"
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-bold text-gray-900 uppercase tracking-wide mb-2">
                  Company <span className="text-orange-600">*</span>
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  required
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-600 text-gray-900"
                  placeholder="Your company name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-bold text-gray-900 uppercase tracking-wide mb-2">
                  Email <span className="text-orange-600">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-600 text-gray-900"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label htmlFor="category" className="block text-sm font-bold text-gray-900 uppercase tracking-wide mb-2">
                  Category <span className="text-orange-600">*</span>
                </label>
                <select
                  id="category"
                  name="category"
                  required
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-600 text-gray-900 cursor-pointer"
                >
                  <option value="">Select partnership type</option>
                  <option value="founding">Founding Partner</option>
                  <option value="show-sponsor">Show Sponsor</option>
                  <option value="brand-sponsor">Brand Sponsor/Partner</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-bold text-gray-900 uppercase tracking-wide mb-2">
                  Message <span className="text-orange-600">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-600 text-gray-900 resize-none"
                  placeholder="Tell us about your goals and how you'd like to work together (max 500 characters)"
                ></textarea>
                <p className="text-sm text-gray-500 mt-2">{formData.message.length}/500 characters</p>
              </div>
            </div>

            {submitStatus === 'success' && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800 font-bold flex items-center gap-2">
                  <i className="ri-check-line text-xl"></i>
                  Thank you! We'll be in touch with partnership details soon.
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 font-bold flex items-center gap-2">
                  <i className="ri-error-warning-line text-xl"></i>
                  Something went wrong. Please try again.
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full mt-8 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 text-white px-10 py-4 rounded-full font-bold text-lg uppercase tracking-wide transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-orange-600/30 whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? 'Sending...' : 'Request Info'}
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}
